import React, { useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Autocomplete,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

interface PaymentHistoryFiltersProps {
  filters: {
    attendeeId?: number;
    startDate?: string;
    endDate?: string;
  };
  onFilterChange: (filters: any) => void;
  attendees: any[];
}

const PaymentHistoryFilters: React.FC<PaymentHistoryFiltersProps> = ({
  filters,
  onFilterChange,
  attendees,
}) => {
  const intl = useIntl();

  // Find selected attendee for display
  const selectedAttendee = useMemo(() => {
    if (!filters.attendeeId || !attendees) return null;
    return (
      attendees.find((attendee) => attendee.id === filters.attendeeId) || null
    );
  }, [filters.attendeeId, attendees]);

  const handleFilterChange = (field: string, value: any) => {
    onFilterChange({
      ...filters,
      [field]: value === "" ? undefined : value,
    });
  };

  const handleAttendeeChange = (event: any, newValue: any) => {
    handleFilterChange("attendeeId", newValue ? newValue.id : "");
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    onFilterChange({
      startDate: today,
      endDate: today,
    });
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <FilterIcon sx={{ mr: 1 }} />
        <FormattedMessage
          id="admin.paymentHistory.filters"
          defaultMessage="Filters"
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={attendees || []}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            value={selectedAttendee}
            onChange={handleAttendeeChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: "admin.paymentHistory.attendee",
                  defaultMessage: "Attendee",
                })}
                placeholder={intl.formatMessage({
                  id: "admin.paymentHistory.searchAttendees",
                  defaultMessage: "Search attendees...",
                })}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Box component="span" fontWeight="bold">
                    {option.firstName} {option.lastName}
                  </Box>
                  <Box
                    component="div"
                    color="text.secondary"
                    fontSize="0.875rem"
                  >
                    {option.email}
                  </Box>
                </Box>
              </Box>
            )}
            noOptionsText={intl.formatMessage({
              id: "admin.paymentHistory.noAttendeesFound",
              defaultMessage: "No attendees found",
            })}
            clearText={intl.formatMessage({
              id: "admin.common.clear",
              defaultMessage: "Clear",
            })}
            disableClearable={false}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={intl.formatMessage({
              id: "admin.paymentHistory.startDate",
              defaultMessage: "Start Date",
            })}
            value={filters.startDate || ""}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={intl.formatMessage({
              id: "admin.paymentHistory.endDate",
              defaultMessage: "End Date",
            })}
            value={filters.endDate || ""}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            size="small"
          >
            <FormattedMessage
              id="admin.paymentHistory.clearFilters"
              defaultMessage="Clear"
            />
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PaymentHistoryFilters;
